import React from 'react';
import { useFormikContext } from 'formik';

import CardDialog from '~/components/CardDialog';
import { Text } from '~/components/core';
import MenuItem from '~/components/core/Atomic/MenuItem';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import { MultiSelectTextFieldFormik, TextFieldFormik } from '~/components/TextFieldFormik';
import { DATA_EXPORT } from '~/Types';

import HourAndEndDateFields from '../shared/HourAndEndDateFields';
import { SHARED_EXPORT_TEXTS } from '../shared/texts';

import { DAY_OPTIONS, type DayOfWeek } from './dayOfWeek';

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, i) => i + 1);

const RECURRENCE_OPTIONS = [
  { text: SHARED_EXPORT_TEXTS.RECURRENCE_DAILY, optionValue: 'daily' },
  { text: SHARED_EXPORT_TEXTS.RECURRENCE_MONTHLY, optionValue: 'monthly' },
];

interface ScheduledReportScheduleValues {
  recurrence_type: 'daily' | 'monthly';
  export_days: DayOfWeek[];
  day_of_month?: number;
  export_hour: string;
  is_end_date?: boolean;
  end_date?: string;
}

const ScheduledReportScheduleSection: React.FC = () => {
  const { isSubmitting, values } = useFormikContext<ScheduledReportScheduleValues>();
  const isMonthly = values.recurrence_type === 'monthly';

  return (
    <div className="-m-8 p-12">
      <CardDialog containerClassName="bg-slate-200" noCardTitle trackAlt="Schedule Details">
        <Text className="mb-20 font-bold" variant={Text.VARIANTS.LG}>
          {SHARED_EXPORT_TEXTS.SCHEDULE_DETAILS_TITLE}
        </Text>
        <div className="w-1/2 gap-16">
          <RadioButtonGroupFormik
            id="recurrence_type"
            label={SHARED_EXPORT_TEXTS.RECURRENCE_LABEL}
            options={RECURRENCE_OPTIONS}
            buttonVariant={RadioButtonGroupFormik.BUTTON_VARIANT.DEFAULT}
            direction="row"
            showOnly={isSubmitting}
          />
          {isMonthly ? (
            <TextFieldFormik
              id="day_of_month"
              label={SHARED_EXPORT_TEXTS.DAY_OF_MONTH_LABEL}
              select
              fullWidth
              disabled={isSubmitting}
            >
              {/* @ts-expect-error TextFieldFormik types don't support MenuItem children when used as select */}
              {DAY_OF_MONTH_OPTIONS.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </TextFieldFormik>
          ) : (
            <>
              <Text variant={Text.VARIANTS.SM}>{SHARED_EXPORT_TEXTS.DAILY_REPEAT_ON}</Text>
              <MultiSelectTextFieldFormik
                id="export_days"
                disabled={isSubmitting}
                fullWidth
                renderOption={(option: string) => DATA_EXPORT.DAYS[option as keyof typeof DATA_EXPORT.DAYS].abbr}
                options={[...DAY_OPTIONS].sort()}
                renderValue={() => ''}
                withOptionChips
              />
            </>
          )}
          <HourAndEndDateFields />
        </div>
      </CardDialog>
    </div>
  );
};

export default ScheduledReportScheduleSection;
